<template>
    <tailwind-layout title="Property Lease History">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center">
                <!-- Block for lg and larger -->
                <div>
                    <h1 class="font-semibold text-2xl leading-tight">Property Lease History</h1>
                    <h2 class="text-2xl font-bold mt-5">{{ property.address_1 }}</h2>
                    <p class="text-sm font-medium underline">
                        <a :href="'https://www.google.com/maps?q=' + property.map_address" target="_blank">{{
                            property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5" aria-hidden="true" /></a>
                    </p>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link class="whiteButton" :href="route('properties.show', property.id) + '?tab=rent-payments'">
                        <FontAwesome :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden">
                <!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="font-semibold text-2xl leading-tight">Property Lease History</h1>
                        <h2 class="text-2xl font-bold mt-5">{{ property.address_1 }}</h2>
                        <p class="text-sm font-medium underline">
                            <a :href="'https://www.google.com/maps?q=' + property.map_address" target="_blank">{{
                                property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5" aria-hidden="true" /></a>
                        </p>
                    </div>
                </div>
            </div>
        </template>

        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch">
                    <div class="sm:w-2/12 pr-4 order-2 sm:order-1 pt-5">
                        <select v-model="form.paginate"
                            :disabled="isSharedViewOnly"
                            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md"
                            aria-label="Number of items per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="sm:w-2/12 order-5 sm:order-5 flex items-start ml-auto pt-5 justify-end">
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                <span @click="sortTable('amount')">
                                                    Tenant
                                                    <icon v-show="form.sort_by === 'amount'"
                                                        :name="form.sort_direction_icon" />
                                                    <icon v-if="!form.sort_by || form.sort_by !== 'amount'"
                                                        :name="'sort'" />
                                                </span>
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                <span @click="sortTable('created_at')">
                                                    Lease Term
                                                    <icon v-show="form.sort_by === 'created_at'"
                                                        :name="form.sort_direction_icon" />
                                                    <icon v-if="!form.sort_by || form.sort_by !== 'created_at'"
                                                        :name="'sort'" />
                                                </span>
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Status
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(lease_term, index) in lease_terms" :key="lease_term.id"
                                            class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer">
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <span v-if="lease_term?.tenant?.name">
                                                    {{ lease_term.tenant.name }}
                                                </span>
                                                <span v-else-if="lease_term?.invitation?.email">
                                                    {{ lease_term?.invitation?.email }} (invitation sent)
                                                </span>
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ lease_term.lease_start }} - {{ lease_term.lease_end }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ lease_term.status }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <inertia-link title="Payment Detail"
                                                    :href="route('rented-properties.payments', lease_term.id)"
                                                    class="text-rent-risk-blue-400 hover:text-indigo-900 ml-2">
                                                    <font-awesome-icon icon="fa-dollar" />
                                                </inertia-link>

                                                <inertia-link v-if="$page.props.user.role === 'agent' && !isSharedViewOnly"
                                                    title="Edit Lease Information"
                                                    :href="route('rented-properties.edit-lease', lease_term.id)"
                                                    class="text-rent-risk-blue-400 hover:text-indigo-900 ml-2">
                                                    <font-awesome-icon icon="fa-edit" />
                                                </inertia-link>
                                                <span
                                                    v-else-if="$page.props.user.role === 'agent' && isSharedViewOnly"
                                                    title="Edit Lease Information"
                                                    class="text-gray-300 ml-2 cursor-not-allowed"
                                                >
                                                    <font-awesome-icon icon="fa-edit" />
                                                </span>
                                            </td>
                                        </tr>
                                        <tr v-show="!lease_terms.length">
                                            <td colspan="5"
                                                class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                                No history found.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(lease_term, index) in lease_terms" :key="lease_term.id" :class="[property.deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">{{ lease_term.lease_start }} - {{ lease_term.lease_end }}</h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Tenant:</span> {{ lease_term.tenant.name }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Status:</span> {{ lease_term.status }}</h4>
                                        </div>
                                        <div>
                                            <div :class="[property.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link :href="route('rented-properties.payments', lease_term.id)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-eyes" />
                                                        Payments
                                                    </inertia-link>
                                                </div>
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <inertia-link v-if="!isSharedViewOnly" :href="route('rented-properties.edit-lease', lease_term.id)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-edit" />
                                                        Edit
                                                    </inertia-link>
                                                    <span
                                                        v-else
                                                        class="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-400 cursor-not-allowed select-none"
                                                    >
                                                        <font-awesome-icon icon="fa-edit" />
                                                        Edit
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';

export default {
    components: {
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesome: FontAwesomeIcon
    },
    props: {
        filters: Object,
        lease_terms: Object,
        property: Object,
        property_access: {
            type: String,
            default: 'owner',
        },
    },
    computed: {
        isSharedViewOnly() {
            return this.property_access === 'shared_view'
        },
    },
    data() {
        return {
            faChevronLeft,
            form: {
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate) ? this.filters.paginate : 25,
                sort_by: this.filters.sort_by,
                sort_direction_icon: (this.filters.sort_direction === 'desc') ? 'sort-up' : 'sort-down'
            },
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('rented-properties.lease-history', this.property.id), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        sortTable(column) {
            if (this.isSharedViewOnly) {
                return
            }
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc') ? 'asc' : 'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc') ? 'sort-up' : 'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
